import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary';
import Logo from '../components/ui/Logo';
import GuestOnlyRoute from './GuestOnlyRoute';
import { RequireStudent, RequireInstructor } from './RequireAuth';
import InstructorStudentRoute from './InstructorStudentRoute';
import { PATHS } from '../navigation/paths';
import { lazyWithReload as lazy } from '../utils/lazyWithReload';

import LoginScreen from '../pages/LoginScreen';

const LandingPage = lazy(() => import('../pages/LandingPage'));
const AydinlatmaMetni = lazy(() => import('../pages/AydinlatmaMetni'));
const StudentPortal = lazy(() => import('../pages/student/StudentPortal'));
const InstructorLayout = lazy(() => import('../pages/instructor/InstructorLayout'));
const InstructorDashboard = lazy(() => import('../pages/instructor/InstructorDashboard'));
const StudentsList = lazy(() => import('../pages/instructor/StudentsList'));
const SchedulePage = lazy(() => import('../pages/instructor/SchedulePage'));
const PaymentsPage = lazy(() => import('../pages/instructor/PaymentsPage'));
const MonthlyReport = lazy(() => import('../pages/instructor/MonthlyReport'));
const InstructorAccount = lazy(() => import('../pages/instructor/InstructorAccount'));

const RouteLoadingFallback = () => (
  <div className="min-h-[100dvh] flex flex-col items-center justify-center gap-6 bg-slate-50 dark:bg-slate-900">
    <Logo size={56} variant="nav" />
    <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-600 border-t-transparent" />
  </div>
);

const LazyRoute = ({ children }) => (
  <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>
);

/** Uygulama route ağacı — yalnızca URL → bileşen eşlemesi (SRP) */
const AppRoutes = () => (
  <Routes>
    <Route
      path={PATHS.LANDING}
      element={
        <LazyRoute>
          <LandingPage />
        </LazyRoute>
      }
    />
    <Route
      path={PATHS.LOGIN}
      element={
        <GuestOnlyRoute>
          <LoginScreen />
        </GuestOnlyRoute>
      }
    />
    <Route
      path={PATHS.KVKK}
      element={
        <LazyRoute>
          <AydinlatmaMetni />
        </LazyRoute>
      }
    />

    <Route
      path={PATHS.STUDENT}
      element={
        <RequireStudent>
          <ErrorBoundary>
            <LazyRoute>
              <StudentPortal />
            </LazyRoute>
          </ErrorBoundary>
        </RequireStudent>
      }
    />
    <Route
      path={`${PATHS.STUDENT}/account`}
      element={
        <RequireStudent>
          <ErrorBoundary>
            <LazyRoute>
              <StudentPortal />
            </LazyRoute>
          </ErrorBoundary>
        </RequireStudent>
      }
    />
    <Route
      path={`${PATHS.STUDENT}/account/:section`}
      element={
        <RequireStudent>
          <ErrorBoundary>
            <LazyRoute>
              <StudentPortal />
            </LazyRoute>
          </ErrorBoundary>
        </RequireStudent>
      }
    />
    <Route
      path={`${PATHS.STUDENT}/:tab`}
      element={
        <RequireStudent>
          <ErrorBoundary>
            <LazyRoute>
              <StudentPortal />
            </LazyRoute>
          </ErrorBoundary>
        </RequireStudent>
      }
    />

    <Route
      path={PATHS.INSTRUCTOR}
      element={
        <RequireInstructor>
          <ErrorBoundary>
            <LazyRoute>
              <InstructorLayout />
            </LazyRoute>
          </ErrorBoundary>
        </RequireInstructor>
      }
    >
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route
        path="dashboard"
        element={
          <LazyRoute>
            <InstructorDashboard />
          </LazyRoute>
        }
      />
      <Route path="requests" element={<Navigate to="dashboard" replace />} />
      <Route
        path="students"
        element={
          <LazyRoute>
            <StudentsList />
          </LazyRoute>
        }
      />
      <Route path="students/:studentId" element={<InstructorStudentRoute />} />
      <Route
        path="schedule"
        element={
          <LazyRoute>
            <SchedulePage />
          </LazyRoute>
        }
      />
      <Route
        path="payments"
        element={
          <LazyRoute>
            <PaymentsPage />
          </LazyRoute>
        }
      />
      <Route
        path="report"
        element={
          <LazyRoute>
            <MonthlyReport />
          </LazyRoute>
        }
      />
      <Route
        path="account"
        element={
          <LazyRoute>
            <InstructorAccount />
          </LazyRoute>
        }
      />
      <Route
        path="account/:section"
        element={
          <LazyRoute>
            <InstructorAccount />
          </LazyRoute>
        }
      />
      {/* Eski bağlantılar ve yer imleri hesap sayfasına düşer */}
      <Route path="settings" element={<Navigate to="account" replace />} />
    </Route>

    <Route path="*" element={<Navigate to={PATHS.LANDING} replace />} />
  </Routes>
);

export default AppRoutes;
